/**
 * LLMSettings Page (SPEC §1.10-§1.11, Track 5).
 *
 * Bring-your-own-key: the tenant connects their own LLM API key (stored in
 * the encrypted per-tenant vault) and picks models per routing tier. The
 * REASONING tier is a platform floor — only approved frontier models are
 * offered, and the floor is rendered as fixed policy, not a toggle.
 */

import { useCallback, useEffect, useState } from 'react';
import {
  Badge,
  Banner,
  BlockStack,
  Button,
  Card,
  Divider,
  InlineStack,
  Layout,
  Page,
  Select,
  Spinner,
  Text,
  TextField,
} from '@shopify/polaris';
import type {
  ByokStatus,
  LLMModel,
  TierConfig,
} from '../types/llmSettings';
import {
  connectByokKey,
  disconnectByokKey,
  getByokStatus,
  getTierConfig,
  listModels,
  updateTierConfig,
} from '../services/llmSettingsApi';

export function LLMSettings() {
  const [byok, setByok] = useState<ByokStatus | null>(null);
  const [models, setModels] = useState<LLMModel[]>([]);
  const [tierConfig, setTierConfig] = useState<TierConfig | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const [keyInput, setKeyInput] = useState('');
  const [isSavingKey, setIsSavingKey] = useState(false);

  const [cheapChoice, setCheapChoice] = useState('');
  const [reasoningChoice, setReasoningChoice] = useState('');
  const [isSavingTiers, setIsSavingTiers] = useState(false);

  const fetchAll = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const [byokStatus, modelList, tiers] = await Promise.all([
        getByokStatus(),
        listModels(),
        getTierConfig(),
      ]);
      setByok(byokStatus);
      setModels(modelList);
      setTierConfig(tiers);
      setCheapChoice(tiers.cheap.configured_model_id || '');
      setReasoningChoice(tiers.reasoning.configured_model_id || '');
    } catch (err) {
      console.error('Failed to load LLM settings:', err);
      setError('Failed to load LLM settings. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const handleConnectKey = async () => {
    setIsSavingKey(true);
    setError(null);
    try {
      const status = await connectByokKey(keyInput);
      setByok(status);
      setKeyInput('');
      setNotice(
        'LLM key connected. Your agents run on your key — you bear your ' +
          'own inference cost directly.'
      );
    } catch (err) {
      const detail =
        (err as Error & { detail?: string }).detail || 'Please try again.';
      setError(`Could not connect key: ${detail}`);
    } finally {
      setIsSavingKey(false);
    }
  };

  const handleDisconnectKey = async () => {
    setIsSavingKey(true);
    setError(null);
    try {
      const status = await disconnectByokKey();
      setByok(status);
      setNotice(
        'LLM key disconnected. Agent reasoning is paused until a new key ' +
          'is connected — the platform key is never used for your agents.'
      );
    } catch (err) {
      const detail =
        (err as Error & { detail?: string }).detail || 'Please try again.';
      setError(`Could not disconnect key: ${detail}`);
    } finally {
      setIsSavingKey(false);
    }
  };

  const handleSaveTiers = async () => {
    setIsSavingTiers(true);
    setError(null);
    try {
      const updated = await updateTierConfig({
        cheap_model_id: cheapChoice,
        reasoning_model_id: reasoningChoice,
      });
      setTierConfig(updated);
      setCheapChoice(updated.cheap.configured_model_id || '');
      setReasoningChoice(updated.reasoning.configured_model_id || '');
      setNotice('Model configuration saved.');
    } catch (err) {
      const detail =
        (err as Error & { detail?: string }).detail || 'Please try again.';
      setError(`Could not save model configuration: ${detail}`);
    } finally {
      setIsSavingTiers(false);
    }
  };

  const eligibleReasoningModels = models.filter(
    m => m.reasoning_tier_eligible
  );

  const cheapOptions = [
    { label: 'Platform default', value: '' },
    ...models.map(m => ({ label: m.display_name, value: m.model_id })),
  ];

  // The reasoning select is built ONLY from eligible models — the floor is
  // structural: ineligible models are not offered, and the server refuses
  // them anyway.
  const reasoningOptions = [
    { label: 'Platform default (frontier)', value: '' },
    ...eligibleReasoningModels.map(m => ({
      label: m.display_name,
      value: m.model_id,
    })),
  ];

  return (
    <Page
      title="AI settings"
      subtitle="Use your own API key and choose which AI model each tier of work runs on."
    >
      <Layout>
        <Layout.Section>
          <BlockStack gap="400">
            {error && (
              <Banner tone="critical" onDismiss={() => setError(null)}>
                {error}
              </Banner>
            )}
            {notice && (
              <Banner tone="info" onDismiss={() => setNotice(null)}>
                {notice}
              </Banner>
            )}

            {isLoading && (
              <InlineStack align="center">
                <Spinner size="large" />
              </InlineStack>
            )}

            {!isLoading && byok && (
              <Card>
                <BlockStack gap="300">
                  <InlineStack gap="200" blockAlign="center">
                    <Text as="h3" variant="headingSm">
                      Your LLM key (BYOK)
                    </Text>
                    <Badge tone={byok.has_key ? 'success' : 'attention'}>
                      {byok.has_key
                        ? `Connected ${byok.key_hint || ''}`
                        : 'Not connected'}
                    </Badge>
                  </InlineStack>
                  <Text as="p" variant="bodySm" tone="subdued">
                    Your agents run on your own key, stored encrypted in your
                    tenant vault — you bear your own inference cost directly.
                    The platform key is never used for your agent runs. If
                    your key hits its provider limit, agents pause gracefully
                    and you are notified.
                  </Text>
                  <InlineStack gap="200" blockAlign="end">
                    <TextField
                      label={byok.has_key ? 'Replace key' : 'Connect key'}
                      value={keyInput}
                      onChange={setKeyInput}
                      autoComplete="off"
                      type="password"
                      placeholder="sk-or-..."
                    />
                    <Button
                      variant="primary"
                      onClick={handleConnectKey}
                      loading={isSavingKey}
                      disabled={keyInput.length < 8}
                    >
                      {byok.has_key ? 'Replace' : 'Connect'}
                    </Button>
                    {byok.has_key && (
                      <Button
                        tone="critical"
                        onClick={handleDisconnectKey}
                        loading={isSavingKey}
                      >
                        Disconnect
                      </Button>
                    )}
                  </InlineStack>
                </BlockStack>
              </Card>
            )}

            {!isLoading && tierConfig && (
              <Card>
                <BlockStack gap="300">
                  <Text as="h3" variant="headingSm">
                    Models per tier
                  </Text>

                  <Banner tone="info">
                    <Text as="p" variant="bodySm">
                      {tierConfig.reasoning_floor.description}
                    </Text>
                  </Banner>

                  <Select
                    label="Reasoning tier (platform floor applies)"
                    options={reasoningOptions}
                    value={reasoningChoice}
                    onChange={setReasoningChoice}
                    helpText={`Effective model: ${
                      tierConfig.reasoning.effective_model_id ||
                      'unresolved — contact support'
                    }. Only platform-approved frontier models are offered.`}
                  />

                  <Select
                    label="Cheap / high-volume tier (freely substitutable)"
                    options={cheapOptions}
                    value={cheapChoice}
                    onChange={setCheapChoice}
                    helpText={`Effective model: ${
                      tierConfig.cheap.effective_model_id || 'platform default'
                    }`}
                  />

                  <Divider />

                  <InlineStack align="end">
                    <Button
                      variant="primary"
                      onClick={handleSaveTiers}
                      loading={isSavingTiers}
                    >
                      Save model configuration
                    </Button>
                  </InlineStack>
                </BlockStack>
              </Card>
            )}
          </BlockStack>
        </Layout.Section>
      </Layout>
    </Page>
  );
}

export default LLMSettings;
